import type Intl from 'ember-intl/services/intl';
import type { TasApplicationExtension as TasApplication } from '../../data/resources/tas-application.ext';
import calculatePrePercentageValue from './calculate-pre-percentage-value';

/**
 * Builds the localized requested-total calculation note. The caller supplies owner-bound intl
 * because WarpDrive records have no owner.
 */
export function reimbursementCalculationFormulaNote(
  application: TasApplication,
  intl: Intl
): string {
  if (application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByPercent) {
    const reimbursementPercentage =
      application.tasProgramInstance?.tasProgramTemplate?.reimbursementPercentage;
    const prePercentageValue = calculatePrePercentageValue(
      application.requestedTotal,
      reimbursementPercentage
    );
    const formattedOriginalTotal = intl.formatNumber(prePercentageValue / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
    const formattedTotal = intl.formatNumber(application.requestedTotal / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
    return intl.t(
      'tuition_assistance.application_details.reimbursement_calculation_by_percent_formula',
      {
        percent: reimbursementPercentage,
        originalTotalInDollars: formattedOriginalTotal,
        totalInDollars: formattedTotal,
      }
    );
  }

  if (application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByCredit) {
    return explanationOfReimbursementByCredit(application, intl);
  }

  if (application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByGPA) {
    const formattedRequestedTotal = intl.formatNumber(application.requestedTotal / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });

    return intl.t(
      'tuition_assistance.application_details.reimbursement_calculation_by_gpa_formula',
      {
        gpa: application.gradePointAverage,
        requestedTotal: formattedRequestedTotal,
      }
    );
  }
  return '';
}

/** Builds the localized reimbursement-by-credit explanation using caller-supplied, owner-bound intl. */
export function explanationOfReimbursementByCredit(
  application: TasApplication,
  intl: Intl
): string {
  if (!application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByCredit) {
    return '';
  }

  // Reimbursement by credit calculations are done before subtracting scholarship amount
  const requestedTotalBeforeScholarships = application.requestedTotalAllCourses;

  const rate = application.tasProgramInstance?.tasProgramTemplate?.ratePerCredit || 0;
  // A falsy/zero rate makes `noOfCredits = total / rate` Infinity/NaN, so the by-credit
  // note can't be computed meaningfully. Treat a falsy rate as invalid and bail with the
  // same empty string this function already returns when not calculating by credit — this
  // matches `TasCourseExtension#reimbursementTotalByCredit`, which returns 0 for a falsy rate.
  if (!rate) {
    return '';
  }
  const formattedRate = intl.formatNumber(rate / 100, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
  const formattedTotalBeforeScholarships = intl.formatNumber(
    requestedTotalBeforeScholarships / 100,
    {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }
  );
  return intl.t(
    'tuition_assistance.application_details.reimbursement_calculation_by_credit_formula',
    {
      rateInDollars: formattedRate,
      noOfCredits: requestedTotalBeforeScholarships / rate,
      totalInDollars: formattedTotalBeforeScholarships,
    }
  );
}
